import { useMemo, useState, ChangeEvent } from "react";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { getRedirectUrlWithoutHostname } from "@iddataweb/idw-common-ui/utils";
import { resources } from "@constants/hardcoded-strings";
import { DefaultModal } from "@containers/common/modals/default-modal/default-modal";
import { InputFormGroup } from "@containers/common/input-form-group/input-form-group";
import { ErrorAlert } from "@containers/common/error-alert/error-alert";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { LandingPageTypes } from "@enums/landing-page-types";
import styles from "./modify-landing-page-name-modal.less";

type ModifyLandingPageNameModalProps = {
    landingPageType: LandingPageTypes;
    landingPageName: string;
    onClickContinue: (newRedirectUrl: string) => void;
    onModalClose: () => void;
};


export const ModifyLandingPageNameModal = (props: ModifyLandingPageNameModalProps) => {
    const {
        landingPageType,
        landingPageName,
        onClickContinue,
        onModalClose
    } = props;

    const getCustomLandingPageEnd = (landingPage: string) => {
        const lpWithDisplay = getRedirectUrlWithoutHostname(landingPage);
        const splitString = lpWithDisplay.split("display/");
        if (splitString.length > 1) {
            return splitString[1];
        }
        return lpWithDisplay;
    };

    const [state, setState] = useState({
        customerValue: landingPageName,
        customValue: getCustomLandingPageEnd(landingPageName)
    });

    const onChangeLandingPageName = (event: ChangeEvent<HTMLInputElement>) => {
        if (landingPageType === LandingPageTypes.CUSTOM) {
            setState({
                ...state,
                customValue: event.target.value
            });
        } else if (landingPageType === LandingPageTypes.CUSTOMER) {
            setState({
                ...state,
                customerValue: event.target.value
            })
        }
    };

    const onClickSave = () => {
        if (landingPageType === LandingPageTypes.CUSTOM) {
            const originalRedirectUrlWithoutHostname = getRedirectUrlWithoutHostname(landingPageName);
            const hostnameArray = landingPageName.split(originalRedirectUrlWithoutHostname);
            if (hostnameArray.length > 0) {
                const hostname = hostnameArray[0];
                onClickContinue(hostname + "display/" + state.customValue);
                onModalClose();
            }
        } else if (landingPageType === LandingPageTypes.CUSTOMER) {
            onClickContinue(state.customerValue);
            onModalClose();
        }
    };

    const customValidationError = useMemo(() => {
        if (landingPageType === LandingPageTypes.CUSTOM) {
            if (!state.customValue) {
                return <ErrorAlert><span>{resources.modals.modifyLandingPageName.inputs.landingPage.validation.empty}</span></ErrorAlert>;
            } else if (state.customValue.includes(" ")) {
                return <ErrorAlert><span>{resources.modals.modifyLandingPageName.inputs.landingPage.validation.noSpaces}</span></ErrorAlert>
            }
        }

        return null;
    }, [state.customValue]);

    const customerValidationError = useMemo(() => {
        if (landingPageType === LandingPageTypes.CUSTOMER) {
            if (!state.customerValue) {
                return <ErrorAlert><span>{resources.modals.modifyLandingPageName.inputs.redirectUrl.validation.empty}</span></ErrorAlert>;
            } else if (state.customerValue.includes(" ")) {
                return <ErrorAlert><span>{resources.modals.modifyLandingPageName.inputs.redirectUrl.validation.noSpaces}</span></ErrorAlert>
            } else if (state.customerValue.includes("/display")) {
                return <ErrorAlert><span>{resources.modals.addLandingPage.inputs.redirectUrl.validation.customRedirectNotAllowed}</span></ErrorAlert>
            } 
        }

        return null;
    }, [state.customerValue]);

    const beforeInputElement = useMemo(() => {
        return <div className={styles.displayText}><span>display/</span></div>;
    }, []);

    return (
        <DefaultModal
            automationId="modify-landing-page-name-modal"
            ariaLabelledBy="modify-landing-page-name-title"
            onHide={onModalClose}
            backdrop={"static"}
            keyboard={false}
            isMaxWidth={false}
        >
            <Modal.Header id={"modify-landing-page-name-title"} closeButton>
                <Modal.Title>{resources.modals.modifyLandingPageName.title(landingPageType)}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {landingPageType === LandingPageTypes.CUSTOM && (
                    <>
                        <InputFormGroup
                            ariaLabel={resources.modals.modifyLandingPageName.inputs.landingPage.label}
                            automationId={`modify-landing-page-name-input`}
                            controlId={`modify-landing-page-name-input`}
                            disabled={false}
                            isRequired={true}
                            onChange={onChangeLandingPageName}
                            value={state.customValue}
                            beforeInputElement={beforeInputElement}
                            label={resources.modals.addLandingPage.inputs.landingPage.label}
                            labelWidth={LabelWidthTypes.MEDIUM}
                            layout={LayoutTypes.ROW}
                        />
                        {customValidationError}
                    </>
                )}
                {landingPageType === LandingPageTypes.CUSTOMER && (
                    <>
                        <InputFormGroup
                            ariaLabel={resources.modals.modifyLandingPageName.inputs.redirectUrl.label}
                            automationId={`modify-landing-page-name-input`}
                            controlId={`modify-landing-page-name-input`}
                            disabled={false}
                            isRequired={true}
                            onChange={onChangeLandingPageName}
                            value={state.customerValue}
                            label={resources.modals.modifyLandingPageName.inputs.redirectUrl.label}
                            labelWidth={LabelWidthTypes.MEDIUM}
                            layout={LayoutTypes.ROW}
                        />
                        {customerValidationError}
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button id="modify-landing-page-name-cancel-button" variant="secondary" onClick={onModalClose}>
                    {resources.common.buttons.cancel.label}
                </Button>
                <Button id="modify-landing-page-name-submit-button" variant="primary" onClick={onClickSave} disabled={!!customerValidationError || !!customValidationError}>
                    {resources.common.buttons.update.label}
                </Button>
            </Modal.Footer>
        </DefaultModal>
    );
};